import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createCoupon, getCouponByID, editCoupon } from '../../store/actions/coupon/coupon.action';
import CreateCoupon from '../../components/Coupons/CreateCoupon';
import PageLoader from '../../components/Common/PageLoader';

const CreateCouponsContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const isCreating = useSelector((state) => state.coupon.isCreating);
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [couponToBeEdit, setCouponToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const couponCategory = categories.find((category) => category.link === 'coupons');
      if (couponCategory) {
        setAccessMode(couponCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('Coupon category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch coupon for edit mode
  const getCoupon = useCallback(async (couponId) => {
    try {
      const coupon = await dispatch(getCouponByID(couponId));
      setCouponToBeEdit(coupon);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching coupon:', error);
      navigate('/coupons');
    }
  }, [dispatch, navigate]);

  // Handle create coupon
  const handleCreate = useCallback((couponData) => {
    return dispatch(createCoupon(couponData));
  }, [dispatch]);

  // Handle edit coupon
  const handleEdit = useCallback((couponData) => {
    return dispatch(editCoupon(couponData));
  }, [dispatch]);

  // Initialize on mount
  useEffect(() => {
    const currentMode = params?.mode === 'edit' ? 'EDIT' : 'CREATE';
    setMode(currentMode);
    setAccess();

    if (params?.id && currentMode === 'EDIT') {
      getCoupon(params.id);
    } else {
      setIsLoading(false);
    }
  }, [params?.id, params?.mode, getCoupon, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state
  if (isLoading) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateCoupon
        accessMode={accessMode}
        onCreate={handleCreate}
        onEdit={handleEdit}
        initialValues={couponToBeEdit}
        mode={mode}
        isCreating={isCreating}
      />
    </section>
  );
};

export default CreateCouponsContainer;
